import React from "react";
import { API } from "@/lib/api";
import { X } from "lucide-react";

const WA_NUMBER = "6282114294101";
const SESSION_KEY = "batik_popup_ad_shown";
const DELAY_MS = 3000;

/**
 * Full-screen modal ad that appears 3s after Home page mount.
 * Shows up to 1 poster flagged `tampil_popup=true` (newest / lowest urutan first).
 * Shown at most once per browser session (sessionStorage flag).
 */
export default function PopupAd() {
  const [poster, setPoster] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    fetch(`${API}/posters`, { credentials: "omit" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const popups = (Array.isArray(data) ? data : []).filter((p) => p.tampil_popup);
        if (popups.length === 0) return;
        setPoster(popups[0]);
        timerRef.current = setTimeout(() => {
          setOpen(true);
          sessionStorage.setItem(SESSION_KEY, "1");
        }, DELAY_MS);
      })
      .catch(() => {});

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Close on ESC for keyboard users
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open || !poster) return null;

  const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    `Assalamu'alaikum, saya tertarik dengan ${poster.judul}`
  )}`;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 anim-fade-up"
      onClick={() => setOpen(false)}
      data-testid="popup-ad"
    >
      <div
        className="relative bg-card rounded-xl max-w-md w-full overflow-hidden shadow-2xl border border-accent/30"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors"
          aria-label="Tutup iklan"
          data-testid="popup-ad-close"
        >
          <X className="h-4 w-4" />
        </button>

        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block aspect-[3/4] bg-muted"
          data-testid="popup-ad-image-link"
        >
          <img
            src={`${API}/posters/${poster.id}/gambar`}
            alt={poster.alt_text || poster.judul}
            className="w-full h-full object-cover"
          />
        </a>

        <div className="p-5 space-y-3">
          <h3 className="font-serif text-xl text-primary leading-snug">{poster.judul}</h3>
          {poster.deskripsi && (
            <p className="text-sm text-muted-foreground line-clamp-3">{poster.deskripsi}</p>
          )}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm"
            data-testid="popup-ad-wa"
          >
            Tanya via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
